import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertOctagon, CheckCircle2, Download, Loader2, Lock, RefreshCw, Repeat, Search, StopCircle, X } from "lucide-react";

// Format a timestamp's age in human-readable form ("3h", "2d 4h", "12m").
function formatAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0 || Number.isNaN(ms)) return "";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

// RFC 4180 escape.
function csvCell(v: unknown): string {
  const s = v == null ? "" : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

type Row = {
  id: string;
  route_id: string | null;
  route_label: string | null;
  category: string | null;
  severity: string | null;
  eval_run_id: string | null;
  attempts: number;
  last_status: number | null;
  last_error: string | null;
  payload_preview: string | null;
  resolved_at: string | null;
  created_at: string;
  locked_at: string | null;
  locked_by: string | null;
};

type RetryStatus = "queued" | "in_progress" | "succeeded" | "failed" | "skipped_locked" | "cancelled";
type LogLine = { ts: string; text: string; kind: RetryStatus | "info" };
type ProgressState = {
  active: boolean;
  cancelled: boolean;
  batchId: string | null;
  total: number;
  statuses: Record<string, RetryStatus>;
  log: LogLine[];
};

const emptyProgress: ProgressState = {
  active: false, cancelled: false, batchId: null, total: 0, statuses: {}, log: [],
};

const statusColor: Record<RetryStatus, string> = {
  queued: "bg-muted text-muted-foreground",
  in_progress: "bg-primary/15 text-primary",
  succeeded: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  failed: "bg-destructive/15 text-destructive",
  skipped_locked: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  cancelled: "bg-muted text-muted-foreground line-through",
};

type StatusFilter = "open" | "resolved" | "locked" | "all";
type AgeFilter = "any" | "1h" | "24h" | "3d" | "7d";

const AGE_HOURS: Record<AgeFilter, number | null> = {
  any: null, "1h": 1, "24h": 24, "3d": 72, "7d": 168,
};

export function HelpdeskAlertFailuresPanel() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [progress, setProgress] = useState<ProgressState>(emptyProgress);
  const logRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("open");
  const [routeFilter, setRouteFilter] = useState<string>("all");
  const [lockedByFilter, setLockedByFilter] = useState<string>("");
  const [ageFilter, setAgeFilter] = useState<AgeFilter>("any");
  const [search, setSearch] = useState<string>("");

  const load = async () => {
    setLoading(true);
    // Fetch a wider window than the previous panel — client-side filtering
    // handles route/status/locked_by/age/search without extra round-trips.
    const { data, error } = await supabase
      .from("helpdesk_alert_delivery_failures")
      .select("id, route_id, route_label, category, severity, eval_run_id, attempts, last_status, last_error, payload_preview, resolved_at, created_at, locked_at, locked_by")
      .order("created_at", { ascending: false })
      .limit(500);
    setLoading(false);
    if (error) toast.error(`Load failures: ${error.message}`);
    else setRows((data ?? []) as Row[]);
  };

  useEffect(() => { void load(); }, []);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [progress.log.length]);

  const resolve = async (r: Row) => {
    const { error } = await supabase
      .from("helpdesk_alert_delivery_failures")
      .update({ resolved_at: new Date().toISOString() })
      .eq("id", r.id);
    if (error) toast.error(error.message);
    else { toast.success("Marked resolved"); void load(); }
  };

  const routeOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of rows) {
      if (r.route_id) seen.set(r.route_id, r.route_label ?? r.route_id.slice(0, 8));
    }
    return Array.from(seen.entries()).map(([id, label]) => ({ id, label }));
  }, [rows]);

  const filtered = useMemo(() => {
    const ageHrs = AGE_HOURS[ageFilter];
    const ageCutoff = ageHrs != null ? Date.now() - ageHrs * 3600 * 1000 : null;
    const lockedByLower = lockedByFilter.trim().toLowerCase();
    const searchLower = search.trim().toLowerCase();
    return rows.filter((r) => {
      const isLocked = !!r.locked_at && !r.resolved_at &&
        new Date(r.locked_at).getTime() > Date.now() - 5 * 60 * 1000;
      if (statusFilter === "open" && r.resolved_at) return false;
      if (statusFilter === "resolved" && !r.resolved_at) return false;
      if (statusFilter === "locked" && !isLocked) return false;
      if (routeFilter !== "all" && r.route_id !== routeFilter) return false;
      if (lockedByLower && !(r.locked_by ?? "").toLowerCase().includes(lockedByLower)) return false;
      if (ageCutoff != null && new Date(r.created_at).getTime() > ageCutoff) return false;
      if (searchLower) {
        const hay = `${r.route_label ?? ""} ${r.category ?? ""} ${r.last_error ?? ""} ${r.payload_preview ?? ""}`.toLowerCase();
        if (!hay.includes(searchLower)) return false;
      }
      return true;
    });
  }, [rows, statusFilter, routeFilter, lockedByFilter, ageFilter, search]);

  const openCount = rows.filter((r) => !r.resolved_at).length;
  const filteredOpen = filtered.filter((r) => !r.resolved_at);

  const runRetry = async (targetIds: string[], mode: "bulk" | "single") => {
    if (targetIds.length === 0) {
      toast.info("No open failures to retry.");
      return;
    }
    setRetrying(true);
    const seeded: Record<string, RetryStatus> = {};
    for (const id of targetIds) seeded[id] = "queued";
    setProgress({
      active: true,
      cancelled: false,
      batchId: null,
      total: targetIds.length,
      statuses: seeded,
      log: [{
        ts: new Date().toISOString(),
        text: `Starting ${mode} retry of ${targetIds.length} failure${targetIds.length === 1 ? "" : "s"}…`,
        kind: "info",
      }],
    });

    let session;
    try {
      const { data } = await supabase.auth.getSession();
      session = data.session;
    } catch { /* ignore */ }
    if (!session?.access_token) {
      setRetrying(false);
      toast.error("Not signed in — cannot retry.");
      return;
    }

    // AbortController lets the admin cancel an in-progress batch. The edge
    // function checks req.signal.aborted between rows and records `cancelled`
    // in the audit log for anything queued but not started.
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const url = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/helpdesk-alert-retry`;
    let resp: Response;
    try {
      resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ ids: targetIds, mode }),
        signal: ctrl.signal,
      });
    } catch (e) {
      abortRef.current = null;
      setRetrying(false);
      const isAbort = e instanceof DOMException && e.name === "AbortError";
      setProgress((p) => ({
        ...p, active: false, cancelled: isAbort || p.cancelled,
        log: [...p.log, {
          ts: new Date().toISOString(),
          kind: isAbort ? "cancelled" : "failed",
          text: isAbort ? "Batch cancelled by admin." : `Retry failed: ${e instanceof Error ? e.message : String(e)}`,
        }],
      }));
      if (!isAbort) toast.error(`Retry failed: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }

    if (!resp.ok || !resp.body) {
      abortRef.current = null;
      setRetrying(false);
      setProgress((p) => ({ ...p, active: false }));
      const errText = await resp.text().catch(() => "");
      toast.error(`Retry failed: HTTP ${resp.status}${errText ? ` · ${errText.slice(0, 120)}` : ""}`);
      return;
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let doneTotals = { resolved: 0, still_failing: 0, skipped: 0, cancelled: 0 };
    let sawCancelEvent = false;

    const applyEvent = (ev: any) => {
      setProgress((prev) => {
        const next: ProgressState = {
          ...prev, statuses: { ...prev.statuses }, log: [...prev.log],
        };
        const ts = new Date().toISOString();
        switch (ev.type) {
          case "batch_start":
            next.batchId = ev.batch_id;
            next.total = ev.total;
            next.log.push({ ts, kind: "info", text: `Batch ${String(ev.batch_id).slice(0, 8)}… started (${ev.total} rows).` });
            break;
          case "queued":
            next.statuses[ev.failure_id] = "queued";
            break;
          case "in_progress":
            next.statuses[ev.failure_id] = "in_progress";
            next.log.push({ ts, kind: "in_progress", text: `→ ${ev.failure_id.slice(0, 8)} delivering…` });
            break;
          case "succeeded":
            next.statuses[ev.failure_id] = "succeeded";
            next.log.push({ ts, kind: "succeeded", text: `✓ ${ev.failure_id.slice(0, 8)} resolved (HTTP ${ev.status ?? "?"}, ${ev.attempts} attempt${ev.attempts === 1 ? "" : "s"})` });
            break;
          case "failed":
            next.statuses[ev.failure_id] = "failed";
            next.log.push({ ts, kind: "failed", text: `✗ ${ev.failure_id.slice(0, 8)} failed — ${ev.error ?? `HTTP ${ev.status ?? "?"}`}` });
            break;
          case "skipped_locked":
            next.statuses[ev.failure_id] = "skipped_locked";
            next.log.push({ ts, kind: "skipped_locked", text: `⏸ ${ev.failure_id.slice(0, 8)} skipped — ${ev.reason}` });
            break;
          case "cancelled":
            next.statuses[ev.failure_id] = "cancelled";
            next.log.push({ ts, kind: "cancelled", text: `⊘ ${ev.failure_id.slice(0, 8)} cancelled — ${ev.reason ?? "aborted"}` });
            break;
          case "batch_cancelled":
            sawCancelEvent = true;
            next.cancelled = true;
            doneTotals = {
              resolved: ev.resolved ?? 0,
              still_failing: ev.still_failing ?? 0,
              skipped: ev.skipped_locked ?? 0,
              cancelled: ev.cancelled ?? 0,
            };
            next.log.push({ ts, kind: "cancelled", text: `Batch cancelled — resolved ${doneTotals.resolved} · still failing ${doneTotals.still_failing} · skipped ${doneTotals.skipped} · cancelled ${doneTotals.cancelled}.` });
            break;
          case "batch_complete":
            doneTotals = {
              resolved: ev.resolved ?? 0,
              still_failing: ev.still_failing ?? 0,
              skipped: ev.skipped_locked ?? 0,
              cancelled: 0,
            };
            next.log.push({ ts, kind: "info", text: `Batch complete — resolved ${doneTotals.resolved} · still failing ${doneTotals.still_failing} · skipped ${doneTotals.skipped}.` });
            break;
        }
        return next;
      });
    };

    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";
        for (const line of lines) {
          if (!line.trim()) continue;
          try { applyEvent(JSON.parse(line)); }
          catch { /* ignore malformed line */ }
        }
      }
      if (buffer.trim()) {
        try { applyEvent(JSON.parse(buffer)); } catch { /* ignore */ }
      }
    } catch (e) {
      const isAbort = e instanceof DOMException && e.name === "AbortError";
      if (!isAbort) throw e;
      // The stream ended because we aborted client-side — flag any remaining
      // queued rows as cancelled so the UI matches the server outcome.
      setProgress((prev) => {
        const nextStatuses = { ...prev.statuses };
        for (const [k, v] of Object.entries(nextStatuses)) {
          if (v === "queued" || v === "in_progress") nextStatuses[k] = "cancelled";
        }
        return {
          ...prev,
          cancelled: true,
          statuses: nextStatuses,
          log: [...prev.log, {
            ts: new Date().toISOString(),
            kind: "cancelled",
            text: "Batch cancelled by admin — remaining rows will be released as their locks expire.",
          }],
        };
      });
    }

    abortRef.current = null;
    setRetrying(false);
    setProgress((p) => ({ ...p, active: false }));
    if (sawCancelEvent || ctrl.signal.aborted) {
      toast.info("Retry cancelled", {
        description: `Resolved ${doneTotals.resolved} · still failing ${doneTotals.still_failing} · cancelled ${doneTotals.cancelled}`,
      });
    } else {
      toast.success("Retry complete", {
        description: `Resolved ${doneTotals.resolved} · still failing ${doneTotals.still_failing} · skipped ${doneTotals.skipped}`,
      });
    }
    void load();
  };

  const cancelRetry = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  };

  const retryAll = () => runRetry(filteredOpen.map((r) => r.id), "bulk");
  const retryOne = (r: Row) => runRetry([r.id], "single");

  const counts = {
    queued: 0, in_progress: 0, succeeded: 0, failed: 0, skipped_locked: 0, cancelled: 0,
  } as Record<RetryStatus, number>;
  for (const s of Object.values(progress.statuses)) counts[s]++;

  const clearFilters = () => {
    setStatusFilter("open"); setRouteFilter("all");
    setLockedByFilter(""); setAgeFilter("any"); setSearch("");
  };

  const exportCsv = () => {
    if (filtered.length === 0) { toast.info("No rows to export."); return; }
    const header = ["created_at","age","route_label","category","status","locked_by","attempts","last_status","last_error"];
    const rowsOut = filtered.map((r) => {
      const isLocked = !!r.locked_at && !r.resolved_at &&
        new Date(r.locked_at).getTime() > Date.now() - 5 * 60 * 1000;
      const status = r.resolved_at ? "resolved" : isLocked ? "locked" : "open";
      return [
        r.created_at, formatAge(r.created_at), r.route_label ?? "",
        r.category ?? "", status, r.locked_by ?? "",
        r.attempts, r.last_status ?? "", r.last_error ?? "",
      ].map(csvCell).join(",");
    });
    const blob = new Blob([[header.join(","), ...rowsOut].join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    a.href = url;
    a.download = `alert-failures-${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} row${filtered.length === 1 ? "" : "s"}`);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <AlertOctagon className={`size-4 ${openCount ? "text-destructive" : "text-muted-foreground"}`} />
            Alert delivery failures
            {openCount > 0 && <Badge variant="destructive">{openCount} open</Badge>}
          </CardTitle>
          <CardDescription>
            Slack webhook deliveries that exhausted the retry budget (3 attempts with exponential backoff).
            Retries are row-locked so two admins cannot re-send the same failure at once.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {retrying ? (
            <Button variant="destructive" size="sm" onClick={cancelRetry}>
              <StopCircle className="size-3.5 mr-1" /> Cancel batch
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void retryAll()}
              disabled={filteredOpen.length === 0}
            >
              <Repeat className="size-3.5 mr-1" />
              Retry filtered ({filteredOpen.length})
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={filtered.length === 0}>
            <Download className="size-3.5 mr-1" /> Export CSV
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={() => void load()} aria-label="Refresh">
            {loading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="grid gap-3 md:grid-cols-[140px_1fr_180px_140px_auto] items-end">
          <div className="space-y-1">
            <Label className="text-xs">Status</Label>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="locked">Locked (in retry)</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Route</Label>
            <Select value={routeFilter} onValueChange={setRouteFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All routes</SelectItem>
                {routeOptions.map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Locked by (user id)</Label>
            <Input placeholder="uuid fragment…" value={lockedByFilter}
              onChange={(e) => setLockedByFilter(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Age</Label>
            <Select value={ageFilter} onValueChange={(v) => setAgeFilter(v as AgeFilter)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="1h">≤ 1 hour</SelectItem>
                <SelectItem value="24h">≤ 24 hours</SelectItem>
                <SelectItem value="3d">≤ 3 days</SelectItem>
                <SelectItem value="7d">≤ 7 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="size-3.5 mr-1" /> Clear
          </Button>
        </div>
        <div className="relative">
          <Search className="size-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Search route label, category, error, payload…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {(progress.active || progress.log.length > 0) && (
          <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-medium">Retry progress</span>
              {progress.batchId && (
                <span className="font-mono text-[10px] text-muted-foreground">
                  batch {progress.batchId.slice(0, 8)}…
                </span>
              )}
              {progress.cancelled && (
                <Badge variant="outline" className="text-amber-600 dark:text-amber-400">cancelled</Badge>
              )}
              <span className="ml-auto flex flex-wrap items-center gap-1.5">
                <Badge className={statusColor.queued} variant="secondary">Queued {counts.queued}</Badge>
                <Badge className={statusColor.in_progress} variant="secondary">In progress {counts.in_progress}</Badge>
                <Badge className={statusColor.succeeded} variant="secondary">Succeeded {counts.succeeded}</Badge>
                <Badge className={statusColor.failed} variant="secondary">Failed {counts.failed}</Badge>
                {counts.skipped_locked > 0 && (
                  <Badge className={statusColor.skipped_locked} variant="secondary">
                    Skipped {counts.skipped_locked}
                  </Badge>
                )}
                {counts.cancelled > 0 && (
                  <Badge className={statusColor.cancelled} variant="secondary">Cancelled {counts.cancelled}</Badge>
                )}
              </span>
            </div>
            <div
              ref={logRef}
              className="max-h-40 overflow-y-auto rounded bg-background/60 p-2 font-mono text-[11px] leading-relaxed"
            >
              {progress.log.map((l, i) => (
                <div key={i} className="whitespace-pre-wrap">
                  <span className="text-muted-foreground">{l.ts.slice(11, 19)} </span>
                  <span className={l.kind === "failed" ? "text-destructive"
                    : l.kind === "succeeded" ? "text-emerald-600 dark:text-emerald-400"
                    : l.kind === "skipped_locked" ? "text-amber-600 dark:text-amber-400"
                    : l.kind === "cancelled" ? "text-amber-600 dark:text-amber-400"
                    : ""}>{l.text}</span>
                </div>
              ))}
              {progress.active && <div className="text-muted-foreground">…streaming</div>}
            </div>
          </div>
        )}

        <div className="text-xs text-muted-foreground">
          Showing {filtered.length} of {rows.length} rows
          {statusFilter !== "all" && ` · status=${statusFilter}`}
          {routeFilter !== "all" && ` · route=${routeOptions.find((r) => r.id === routeFilter)?.label}`}
          {ageFilter !== "any" && ` · age≤${ageFilter}`}
          {lockedByFilter && ` · locked_by~${lockedByFilter}`}
          {search && ` · search="${search}"`}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                <th className="py-2 pr-4 font-medium">When</th>
                <th className="py-2 pr-4 font-medium">Route</th>
                <th className="py-2 pr-4 font-medium">Category</th>
                <th className="py-2 pr-4 font-medium">Attempts</th>
                <th className="py-2 pr-4 font-medium">Last error</th>
                <th className="py-2 pr-4 font-medium">Retry</th>
                <th className="py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="py-4 text-center"><Loader2 className="size-4 animate-spin inline" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="py-4 text-center text-xs text-muted-foreground">
                  No failures match the current filters.
                </td></tr>
              ) : filtered.map((r) => {
                const rowStatus = progress.statuses[r.id];
                const isLocked = !!r.locked_at && !r.resolved_at &&
                  new Date(r.locked_at).getTime() > Date.now() - 5 * 60 * 1000;
                return (
                  <tr key={r.id} className="border-b border-border/50 last:border-0 align-top">
                    <td className="py-2 pr-4 font-mono text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(r.created_at).toISOString().replace("T", " ").slice(0, 19)}
                    </td>
                    <td className="py-2 pr-4">{r.route_label ?? "(deleted)"}</td>
                    <td className="py-2 pr-4"><Badge variant="secondary">{r.category ?? "any"}</Badge></td>
                    <td className="py-2 pr-4 font-mono text-xs">
                      {r.attempts}
                      {r.last_status != null && <span className="text-muted-foreground"> · HTTP {r.last_status}</span>}
                    </td>
                    <td className="py-2 pr-4 text-xs text-muted-foreground max-w-md truncate" title={r.last_error ?? ""}>
                      {r.last_error ?? "—"}
                    </td>
                    <td className="py-2 pr-4">
                      {rowStatus ? (
                        <Badge className={statusColor[rowStatus]} variant="secondary">{rowStatus.replace("_", " ")}</Badge>
                      ) : isLocked ? (
                        <Badge className={statusColor.in_progress} variant="secondary">
                          <Lock className="size-3 mr-1 inline" />
                          {r.locked_by ? `locked (${r.locked_by.slice(0, 8)})` : "locked"}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-2 text-right">
                      {r.resolved_at ? (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <CheckCircle2 className="size-3.5" /> resolved
                        </span>
                      ) : (
                        <div className="inline-flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => void retryOne(r)}
                            disabled={retrying || isLocked}
                            title={isLocked ? "Another admin is retrying this row" : undefined}
                          >
                            <Repeat className="size-3.5 mr-1" /> Retry
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => void resolve(r)}>Resolve</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default HelpdeskAlertFailuresPanel;
